import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { Search, Download, FileDown, Upload, Plus, Edit2, Trash2, Save, Image as ImageIcon, FileSpreadsheet, X } from "lucide-react";

import {
    fetchFormationsRequest,
    createFormationRequest,
    updateFormationRequest,
    deleteFormationRequest,
    downloadFormationsTemplateRequest,
    importFormationsExcelRequest,
    exportFormationsRequest,
    clearFormationsImportResult,
} from "../../../../redux/actions/formationsActions";

import {
    fetchDomainesRequest,
    fetchThemesRequest,
    fetchThemesByDomaineRequest,
} from "../../../../redux/actions/paramDomainesThemesActions";

import { API_BASE_URL } from "../../../../../api";
import "../RessourcesStyles/FormationManagement.css";

const emptyForm = {
    reference: "",
    nom: "",
    description: "",
    duration: "",
    cout: "",
    nombreMaxGroupe: "",
    ordre: "",

    prerequis: "",
    objectif: "",
    objectif2: "",
    objectif3: "",
    typeDuree: "Jours",
    moyenPedagogiques: "",
    publiqueVise: "",
    type: "Présentielle",
    periods: "",
    niveaux: "",

    domaineId: "",
    themeId: "",

    imageFile: null,
};


const Modal = ({ open, title, onClose, size = "lg", footer, children }) => {
    if (!open) return null;

    const sizeClass = size === "xl" ? "crModal__panel--xl" : size === "lg" ? "crModal__panel--lg" : "";

    return (
        <div className="crModal" role="dialog" aria-modal="true" onMouseDown={onClose}>
            <div className={`crModal__panel ${sizeClass}`} onMouseDown={(e) => e.stopPropagation()}>
                <div className="crModal__header">
                    <div className="crModal__title">{title}</div>
                    <button className="crIconBtn" type="button" onClick={onClose} aria-label="Fermer">
                        <X size={18} />
                    </button>
                </div>
                <div className="crModal__body">{children}</div>
                {footer ? <div className="crModal__footer">{footer}</div> : null}
            </div>
        </div>
    );
};

const FormationManagement = () => {
    const dispatch = useDispatch();

    const { list, loading, bulkLoading, error, importResult } = useSelector(
        (state) => state.formations || {}
    );

    const { domaines, themesFiltered, themes } = useSelector(
        (state) => state.paramDomainesThemes || {}
    );

    // local UI
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [imagePreview, setImagePreview] = useState(null);

    // bulk
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkFile, setBulkFile] = useState(null);
    const [bulkDryRun, setBulkDryRun] = useState(true);
    const [bulkAction, setBulkAction] = useState("add"); // add | edit

    // filters
    const [q, setQ] = useState("");

    useEffect(() => {
        dispatch(fetchFormationsRequest());
        dispatch(fetchDomainesRequest());
        dispatch(fetchThemesRequest());
    }, [dispatch]);

    useEffect(() => {
        if (error) {
            Swal.fire({
                icon: "error",
                title: "Erreur",
                text: typeof error === "string" ? error : "Une erreur est survenue",
            });
        }
    }, [error]);

    const formations = useMemo(() => (Array.isArray(list) ? list : []), [list]);

    const filtered = useMemo(() => {
        const t = (q || "").trim().toLowerCase();
        if (!t) return formations;

        return formations.filter((f) => {
            const ref = (f.reference || "").toLowerCase();
            const desc = (f.description || "").toLowerCase();
            const dom = (f.domaineNom || "").toLowerCase();
            const th = (f.themeNom || "").toLowerCase();
            return ref.includes(t) || desc.includes(t) || dom.includes(t) || th.includes(t);
        });
    }, [formations, q]);
    console.log("formations",formations)
    // ===== Modal handlers =====
    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm);
        setImagePreview(null);
        setIsModalOpen(true);
    };

    const openEdit = (f) => {
        setEditing(f);
        setForm({
            ...emptyForm,
            reference: f.reference || "",
            description: f.description || "",
            duration: f.duration ?? "",
            cout: f.cout ?? "",
            nombreMaxGroupe: f.nombreMaxGroupe ?? "",
            ordre: f.ordre ?? "",

            prerequis: f.prerequis || "",
            objectif: f.objectif || "",
            objectif2: f.objectif2 || "",
            objectif3: f.objectif3 || "",
            typeDuree: f.typeDuree || "Jours",
            moyenPedagogiques: f.moyenPedagogiques || "",
            nom: f.nom || "",
            publiqueVise: f.publiqueVise || "",
            type: f.type || "Présentielle",
            periods: f.periods || "",
            niveaux: f.niveaux || "",

            domaineId: f.domaineId || "",
            themeId: f.themeId || "",
            imageFile: null,
        });

        setImagePreview(
            f.picture ? `${API_BASE_URL}/api/formations/images/${f.picture}` : null
        );

        if (f.domaineId) {
            dispatch(fetchThemesByDomaineRequest(f.domaineId));
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditing(null);
        setForm(emptyForm);
        setImagePreview(null);
    };

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((p) => ({ ...p, [name]: value }));
    };

    const onPickImage = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setForm((p) => ({ ...p, imageFile: file }));
        setImagePreview(URL.createObjectURL(file));
    };

    const onSelectDomaine = (e) => {
        const domaineId = e.target.value;
        setForm((p) => ({ ...p, domaineId, themeId: "" }));
        if (domaineId) {
            dispatch(fetchThemesByDomaineRequest(Number(domaineId)));
        }
    };

    const submit = (e) => {
        e.preventDefault();

        if (!form.reference?.trim()) {
            Swal.fire({ icon: "warning", text: "Référence obligatoire." });
            return;
        }
        if (!form.ordre || String(form.ordre).trim() === "") {
            Swal.fire({ icon: "warning", text: "Ordre obligatoire." });
            return;
        }
        if (!form.domaineId) {
            Swal.fire({ icon: "warning", text: "Veuillez choisir un domaine." });
            return;
        }

        const payload = {
            reference: form.reference.trim(),
            description: form.description || "",
            duration: form.duration === "" ? null : Number(form.duration),
            cout: form.cout === "" ? null : Number(form.cout),
            nombreMaxGroupe: form.nombreMaxGroupe === "" ? null : Number(form.nombreMaxGroupe),
            ordre: Number(form.ordre),

            prerequis: form.prerequis || "",
            objectif: form.objectif || "",
            objectif2: form.objectif2 || "",
            objectif3: form.objectif3 || "",
            typeDuree: form.typeDuree || "",
            moyenPedagogiques: form.moyenPedagogiques || "",
            nom: form.nom || "",
            publiqueVise: form.publiqueVise || "",
            type: form.type || "",
            periods: form.periods || "",
            niveaux: form.niveaux || "",

            domaineId: Number(form.domaineId),
            themeId: form.themeId ? Number(form.themeId) : null,

            imageFile: form.imageFile || null,
        };

        if (editing?.id) {
            dispatch(updateFormationRequest(editing.id, payload));
        } else {
            dispatch(createFormationRequest(payload));
        }

        closeModal();
    };

    const confirmDelete = (f) => {
        Swal.fire({
            title: "Supprimer",
            text: `Supprimer la formation "${f.reference}" ?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Non",
            confirmButtonColor: "#d33",
        }).then((res) => {
            if (res.isConfirmed) dispatch(deleteFormationRequest(f.id));
        });
    };

    // ===== Bulk Excel =====
    const openBulk = () => {
        setBulkOpen(true);
        setBulkFile(null);
        setBulkDryRun(true);
        setBulkAction("add");
        dispatch(clearFormationsImportResult());
    };

    const closeBulk = () => {
        setBulkOpen(false);
        setBulkFile(null);
        dispatch(clearFormationsImportResult());
    };

    const runBulk = () => {
        if (!bulkFile) {
            Swal.fire({ icon: "warning", text: "Veuillez sélectionner un fichier Excel." });
            return;
        }
        dispatch(importFormationsExcelRequest(bulkFile, { dryRun: bulkDryRun, action: bulkAction }));
    };

    const themesToUse = useMemo(() => {
        if (Array.isArray(themesFiltered) && themesFiltered.length) return themesFiltered;
        return Array.isArray(themes) ? themes : [];
    }, [themesFiltered, themes]);

    return (
        <div className="crPage">
            <div className="crHeader">
                <div>
                    <h1>Gestion des formations</h1>
                    <p>Gérez votre catalogue de formations</p>
                </div>
            </div>

            <div className="crWrap">
                <div className="crCard">
                    <div className="crBody">
                        <div className="crFilterSection">
                            <div className="crFilterTitle">
                                <Search size={16} />
                                Filtre de recherche
                            </div>

                            <div className="crFilterGrid">
                                <div className="crField crField--full">
                                    <label>Recherche</label>
                                    <input
                                        type="text"
                                        value={q}
                                        onChange={(e) => setQ(e.target.value)}
                                        placeholder="Réf, description, domaine, thème..."
                                    />
                                </div>
                            </div>

                            <div className="crFilterActions">
                                <button
                                    className="crBtn crBtn--ghost"
                                    onClick={() => dispatch(downloadFormationsTemplateRequest())}
                                    disabled={bulkLoading}
                                    type="button"
                                >
                                    <Download size={16} />
                                    Télécharger template
                                </button>

                                <button
                                    className="crBtn crBtn--ghost"
                                    onClick={() => dispatch(exportFormationsRequest())}
                                    disabled={bulkLoading}
                                    type="button"
                                >
                                    <FileDown size={16} />
                                    Export
                                </button>

                                <button className="crBtn crBtn--primary" onClick={openBulk} type="button">
                                    <Upload size={16} />
                                    Ajout massif
                                </button>

                                <button className="crBtn crBtn--primary" onClick={openCreate} type="button">
                                    <Plus size={16} />
                                    Ajouter une formation
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="crWrap">
                <div className="crCard">
                    <div className="crBody">
                        <div className="crTopRow">
                            <h2 className="crTitle">Formations ({loading ? "..." : filtered.length})</h2>
                        </div>

                        <div className="crTableWrap">
                            <table className="crTable">
                                <thead>
                                <tr>
                                    <th>Référence</th>
                                    <th>Nom</th>
                                    <th>Ordre</th>
                                    <th>Domaine</th>
                                    <th>Thème</th>
                                    <th>Durée</th>
                                    <th>Coût</th>
                                    <th>Image</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="crEmptyCell">
                                            Aucune formation
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((f) => (
                                        <tr key={f.id}>
                                            <td>
                                                <span className="crStrong">{f.reference}</span>
                                            </td>
                                            <td className="crMuted">{f.nom || "—"}</td>
                                            <td className="crMuted">{f.ordre ?? "—"}</td>
                                            <td className="crMuted">{f.domaineNom || "-"}</td>
                                            <td className="crMuted">{f.themeNom || "-"}</td>
                                            <td className="crMuted">{f.duration ?? "-"}</td>
                                            <td className="crMuted">{f.cout ?? "-"}</td>
                                            <td>
                                                {f.picture ? (
                                                    <img
                                                        className="fmThumb"
                                                        src={`${API_BASE_URL}/api/formations/images/${f.picture}`}
                                                        alt="formation"
                                                    />
                                                ) : (
                                                    <span className="crMuted">-</span>
                                                )}
                                            </td>
                                            <td className="is-right">
                                                <div className="crRowActions">
                                                    <button
                                                        className="crIconBtn"
                                                        title="Modifier"
                                                        onClick={() => openEdit(f)}
                                                        type="button"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        className="crIconBtn crIconBtn--danger"
                                                        title="Supprimer"
                                                        onClick={() => confirmDelete(f)}
                                                        type="button"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal Ajout / Modification */}
            <Modal
                open={isModalOpen}
                title={editing ? "Modifier formation" : "Ajouter formation"}
                onClose={closeModal}
                size="xl"
                footer={
                    <>
                        <button className="crBtn crBtn--ghost" onClick={closeModal} disabled={loading} type="button">
                            Annuler
                        </button>
                        <button className="crBtn crBtn--primary" onClick={submit} disabled={loading} type="button">
                            <Save size={16} />
                            {loading ? "En cours..." : editing ? "Enregistrer" : "Créer"}
                        </button>
                    </>
                }
            >
                <form className="crFormContent" onSubmit={submit}>
                    <div className="crFormSection">
                        <h3 className="crFormTitle">Identification</h3>
                        <div className="crFormGrid">
                            <div className="crField">
                                <label>Référence *</label>
                                <input name="reference" value={form.reference} onChange={onChange} />
                            </div>
                            <div className="crField">
                                <label>Nom *</label>
                                <input name="nom" value={form.nom} onChange={onChange} />
                            </div>
                            <div className="crField">
                                <label>Ordre *</label>
                                <input name="ordre" value={form.ordre} onChange={onChange} type="number" />
                            </div>
                            <div className="crField">
                                <label>Type</label>
                                <input
                                    name="type"
                                    value={form.type}
                                    onChange={onChange}
                                    placeholder="Présentielle / Distancielle..."
                                />
                            </div>
                            <div className="crField">
                                <label>Durée</label>
                                <input name="duration" value={form.duration} onChange={onChange} type="number" step="0.1" />
                            </div>
                            <div className="crField">
                                <label>Type durée</label>
                                <select name="typeDuree" value={form.typeDuree} onChange={onChange}>
                                    <option value="Jours">Jours</option>
                                    <option value="Heures">Heures</option>
                                </select>
                            </div>
                            <div className="crField">
                                <label>Coût</label>
                                <input name="cout" value={form.cout} onChange={onChange} type="number" step="0.01" />
                            </div>
                            <div className="crField">
                                <label>Max Groupe</label>
                                <input
                                    name="nombreMaxGroupe"
                                    value={form.nombreMaxGroupe}
                                    onChange={onChange}
                                    type="number"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="crFormSection">
                        <h3 className="crFormTitle">Classification</h3>
                        <div className="crFormGrid">
                            <div className="crField">
                                <label>Domaine *</label>
                                <select name="domaineId" value={form.domaineId} onChange={onSelectDomaine}>
                                    <option value="">-- choisir --</option>
                                    {(domaines || []).map((d) => (
                                        <option key={d.id} value={d.id}>
                                            {d.nom} ({d.reference})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="crField">
                                <label>Thème</label>
                                <select name="themeId" value={form.themeId} onChange={onChange}>
                                    <option value="">-- choisir --</option>
                                    {themesToUse
                                        .filter((t) => !form.domaineId || Number(t.domaineId) === Number(form.domaineId))
                                        .map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {t.nom} ({t.reference})
                                            </option>
                                        ))}
                                </select>
                            </div>

                            <div className="crField">
                                <label>Période</label>
                                <input name="periods" value={form.periods} onChange={onChange} placeholder="T1,T2,T4" />
                            </div>

                            <div className="crField">
                                <label>Niveaux</label>
                                <input name="niveaux" value={form.niveaux} onChange={onChange} placeholder="N1,N2" />
                            </div>
                        </div>
                    </div>

                    <div className="crFormSection">
                        <h3 className="crFormTitle">Contenu</h3>
                        <div className="crFormGrid">
                            <div className="crField crField--full">
                                <label>Description</label>
                                <textarea name="description" value={form.description} onChange={onChange} rows={3} />
                            </div>

                            <div className="crField crField--full">
                                <label>Prérequis</label>
                                <textarea name="prerequis" value={form.prerequis} onChange={onChange} rows={2} />
                            </div>

                            <div className="crField crField--full">
                                <label>Objectif 1</label>
                                <textarea name="objectif" value={form.objectif} onChange={onChange} rows={2} />
                            </div>

                            <div className="crField crField--full">
                                <label>Objectif 2</label>
                                <textarea name="objectif2" value={form.objectif2} onChange={onChange} rows={2} />
                            </div>

                            <div className="crField crField--full">
                                <label>Objectif 3</label>
                                <textarea name="objectif3" value={form.objectif3} onChange={onChange} rows={2} />
                            </div>

                            <div className="crField crField--full">
                                <label>Moyens pédagogiques</label>
                                <textarea
                                    name="moyenPedagogiques"
                                    value={form.moyenPedagogiques}
                                    onChange={onChange}
                                    rows={2}
                                />
                            </div>

                            <div className="crField crField--full">
                                <label>Publique visé</label>
                                <input name="publiqueVise" value={form.publiqueVise} onChange={onChange} />
                            </div>
                        </div>
                    </div>

                    <div className="crFormSection">
                        <h3 className="crFormTitle">Média</h3>
                        <div className="crFormGrid">
                            <div className="crField crField--full">
                                <label>
                                    <span className="fmInlineLabel">
                                        <ImageIcon size={16} />
                                        Image
                                    </span>
                                </label>
                                <input type="file" accept="image/*" onChange={onPickImage} />
                                {imagePreview && (
                                    <div className="fmPreview">
                                        <img src={imagePreview} alt="preview" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <button type="submit" style={{ display: "none" }} />
                </form>
            </Modal>

            {/* Modal Ajout massif */}
            <Modal
                open={bulkOpen}
                title="Ajout massif (Excel)"
                onClose={closeBulk}
                size="lg"
                footer={
                    <>
                        <button
                            className="crBtn crBtn--ghost"
                            type="button"
                            onClick={() => dispatch(downloadFormationsTemplateRequest())}
                            disabled={bulkLoading}
                        >
                            <FileSpreadsheet size={16} />
                            Télécharger template
                        </button>

                        <button className="crBtn crBtn--primary" type="button" onClick={runBulk} disabled={bulkLoading}>
                            <Upload size={16} />
                            {bulkLoading ? "Traitement..." : bulkDryRun ? "Prévisualiser" : "Exécuter"}
                        </button>
                    </>
                }
            >
                <div className="crFormContent">
                    <div className="crFormSection">
                        <h3 className="crFormTitle">Paramètres d'import</h3>
                        <div className="crFormGrid">
                            <div className="crField">
                                <label>Action</label>
                                <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)}>
                                    <option value="add">ADD (nouveaux)</option>
                                    <option value="edit">EDIT (mise à jour via référence)</option>
                                </select>
                            </div>

                            <div className="crField">
                                <label>Mode</label>
                                <label className="fmCheck">
                                    <input
                                        type="checkbox"
                                        checked={bulkDryRun}
                                        onChange={(e) => setBulkDryRun(e.target.checked)}
                                    />
                                    Dry-run (prévisualiser sans insertion)
                                </label>
                            </div>

                            <div className="crField crField--full">
                                <label>Fichier Excel</label>
                                <input
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                                />
                            </div>
                        </div>
                    </div>

                    {importResult && (
                        <div className="crFormSection">
                            <h3 className="crFormTitle">Résultat</h3>

                            <div className="fmImportSummary">
                                <span><b>Action:</b> {importResult.action}</span>
                                <span><b>Dry-run:</b> {String(importResult.dryRun)}</span>
                                <span><b>Total:</b> {importResult.totalRows}</span>
                                <span><b>OK:</b> {importResult.createdCount}</span>
                                <span><b>Ignorées:</b> {importResult.skippedCount}</span>
                                <span><b>Erreurs:</b> {(importResult.errors || []).length}</span>
                            </div>

                            {(importResult.errors || []).length > 0 && (
                                <div className="fmBlock">
                                    <div className="fmBlockTitle">Erreurs</div>
                                    <div className="crTableWrap">
                                        <table className="crTable">
                                            <thead>
                                            <tr>
                                                <th>Ligne</th>
                                                <th>Colonne</th>
                                                <th>Message</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {importResult.errors.map((er, idx) => (
                                                <tr key={idx}>
                                                    <td className="crMuted">{er.row}</td>
                                                    <td><span className="crStrong">{er.column}</span></td>
                                                    <td className="crMuted">{er.message}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {(importResult.preview || []).length > 0 && (
                                <div className="fmBlock">
                                    <div className="fmBlockTitle">Prévisualisation (max 20)</div>
                                    <div className="crTableWrap">
                                        <table className="crTable">
                                            <thead>
                                            <tr>
                                                <th>Référence</th>
                                                <th>Nom</th>
                                                <th>Ordre</th>
                                                <th>Domaine</th>
                                                <th>Thème</th>
                                                <th>Durée</th>
                                                <th>Coût</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {importResult.preview.slice(0, 20).map((p, idx) => (
                                                <tr key={idx}>
                                                    <td><span className="crStrong">{p.reference}</span></td>
                                                    <td className="crMuted">{p.nom}</td>
                                                    <td className="crMuted">{p.ordre}</td>
                                                    <td className="crMuted">{p.domaineReference || p.domaineNom || "-"}</td>
                                                    <td className="crMuted">{p.themeReference || p.themeNom || "-"}</td>
                                                    <td className="crMuted">{p.duration ?? "-"}</td>
                                                    <td className="crMuted">{p.cout ?? "-"}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="fmHint">Affichage limité à 20 lignes.</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Modal>
        </div>


);
};

export default FormationManagement;
